import { Checkbox } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { formatStoreAlias } from '../../utils/formatting';
import { daysOfTheWeek } from '../o2o-capabilities/sfs/constants';
import { ButtonBlack, ButtonRed, ButtonWhite } from '../reusable';

import {
  clampInt,
  deriveBopisFormValues,
  deriveSfsFormValues,
  isBopisRowDirty,
  isBopisRowValid,
  isSfsRowDirty,
  isSfsRowValid,
} from './helpers';
import SfsExceptionsModal from './SfsExceptionsModal';

const SHORT_DAY = {
 friday: 'FRI', monday: 'MON', saturday: 'SAT', sunday: 'SUN', thursday: 'THU', tuesday: 'TUE', wednesday: 'WED'
};

// Clamp the raw input to [min, max] and stringify so the controlled HMS
// inputs always store strings (matching `ISOtoHMS`'s output type). Returns
// an empty string when the user temporarily clears the field so they can
// retype without the value snapping back to "0".
const clampLeadTimePart = (rawValue, max) => {
  const clamped = clampInt(rawValue, 0, max);
  return clamped == null ? '' : String(clamped);
};

// Tiny number input for capacity cells (so the table stays compact).
const NumberCell = ({
  id, value, onChange, isDisabled, isDirty, max = 3000, min = 0,
}) => (
  <td className={`o2o-bulk-num-cell${isDirty ? ' is-dirty' : ''}`}>
    <input
      aria-label={id}
      className="o2o-bulk-cell-input"
      disabled={isDisabled}
      id={id}
      max={max}
      min={min}
      step="1"
      type="number"
      value={value}
      onChange={({ target }) => {
        const clamped = clampInt(target.value, min, max);
        onChange(clamped == null ? 0 : clamped);
      }}
    />
  </td>
);
NumberCell.defaultProps = {
 isDirty: false, isDisabled: false, max: 3000, min: 0
};
NumberCell.propTypes = {
  id: PropTypes.string.isRequired,
  isDirty: PropTypes.bool,
  isDisabled: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.number.isRequired,
};

// Each header cell stacks the column title on top of a single "Apply to all"
// button that copies the FIRST ROW's value for that column to every row.
const ApplyHeader = ({
  title, isDisabled, onApply, label, className,
}) => (
  <th className={className}>
    <div className="o2o-bulk-header-stack">
      <span className="o2o-bulk-header-title" title={title}>{title}</span>
      <ButtonBlack
        className="o2o-bulk-apply-btn"
        isDisabled={isDisabled}
        label={label}
        onClick={onApply}
      />
    </div>
  </th>
);
ApplyHeader.defaultProps = {
  className: '',
  label: '↓ Apply to all',
};
ApplyHeader.propTypes = {
  className: PropTypes.string,
  isDisabled: PropTypes.bool.isRequired,
  label: PropTypes.node,
  onApply: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

const renderBopisHeader = ({
  isDisabled, canApply, applyFromFirstRow,
}) => (
  <tr>
    <th>Store</th>
    <ApplyHeader
      className="o2o-bulk-th-center"
      isDisabled={isDisabled || !canApply}
      title="Enabled"
      onApply={() => applyFromFirstRow('enabled')}
    />
    <ApplyHeader
      className="o2o-bulk-th-center"
      isDisabled={isDisabled || !canApply}
      title="Lead Time (h / m / s)"
      onApply={() => applyFromFirstRow('leadTimeData')}
    />
    <th className="o2o-bulk-row-actions">Actions</th>
  </tr>
);

const renderSfsHeader = ({
  isDisabled, canApply, applyFromFirstRow, applyAllDailyCapacityFromFirstRow, applyExceptionsFromFirstRow,
}) => (
  <>
    <tr>
      <th rowSpan={2}>Store</th>
      <th className="o2o-bulk-th-center" rowSpan={2}>
        <span className="o2o-bulk-header-stack">
          <span className="o2o-bulk-header-title">Enabled</span>
          <ButtonBlack
            className="o2o-bulk-apply-btn"
            isDisabled={isDisabled || !canApply}
            label="↓ Apply to all"
            onClick={() => applyFromFirstRow('enabled')}
          />
        </span>
      </th>
      <th className="o2o-bulk-group-header" colSpan={daysOfTheWeek.length}>
        <span className="o2o-bulk-header-stack">
          <span className="o2o-bulk-header-title">Daily Capacity (units / day)</span>
          <ButtonBlack
            className="o2o-bulk-apply-btn"
            isDisabled={isDisabled || !canApply}
            label="↓ Apply to all"
            onClick={applyAllDailyCapacityFromFirstRow}
          />
        </span>
      </th>
      <th className="o2o-bulk-th-center" rowSpan={2}>
        <span className="o2o-bulk-header-stack">
          <span className="o2o-bulk-header-title">Exceptions</span>
          <ButtonBlack
            className="o2o-bulk-apply-btn"
            isDisabled={isDisabled || !canApply}
            label="↓ Apply to all"
            onClick={applyExceptionsFromFirstRow}
          />
        </span>
      </th>
      <th className="o2o-bulk-row-actions" rowSpan={2}>Actions</th>
    </tr>
    <tr>
      {daysOfTheWeek.map((day) => (
        <th key={day} className="o2o-bulk-th-center o2o-bulk-day-th">
          {SHORT_DAY[day]}
        </th>
      ))}
    </tr>
  </>
);

const EditableValuesTable = ({
  fulfillmentType,
  selectedStores,
  currentValuesCache,
  formValuesByStore,
  isDisabled,
  onUpdateRowValues,
  onResetRow,
  onRemoveStore,
  onApplyEnabledToAll,
  onApplyLeadTimeToAll,
  onApplyDayToAll,
  onApplyExceptionsToAll,
}) => {
  const isBopis = fulfillmentType === 'BOPIS';
  const [exceptionsForStoreId, setExceptionsForStoreId] = useState(null);

  // The first selected store's current form values become the template that
  // gets broadcast when the user clicks an "Apply to all" button.
  const firstStore = selectedStores[0];
  const firstRowValues = firstStore ? formValuesByStore[firstStore.id] : null;
  const firstStoreAlias = firstStore ? formatStoreAlias(firstStore) : null;
  // Disable Apply when there's no first-row data OR there's only one store (nothing to copy to).
  const canApply = !!firstRowValues && selectedStores.length > 1;

  const applyFromFirstRow = (field) => {
    if (!firstRowValues) return;
    if (field === 'enabled') {
      onApplyEnabledToAll(firstRowValues.enabled);
    } else if (field === 'leadTimeData') {
      onApplyLeadTimeToAll(firstRowValues.leadTimeData);
    } else if (firstRowValues.defaultCapacity && firstRowValues.defaultCapacity[field]) {
      onApplyDayToAll(field, firstRowValues.defaultCapacity[field].maxUnits);
    }
  };

  // Broadcasts ALL seven daily capacity values from the first row to every
  // other row in one click — replaces seven per-day buttons in the header.
  const applyAllDailyCapacityFromFirstRow = () => {
    if (!firstRowValues || !firstRowValues.defaultCapacity) return;
    daysOfTheWeek.forEach((day) => {
      const cap = firstRowValues.defaultCapacity[day];
      if (cap && Number.isFinite(cap.maxUnits)) {
        onApplyDayToAll(day, cap.maxUnits);
      }
    });
  };

  const applyExceptionsFromFirstRow = () => {
    if (!firstRowValues) return;
    onApplyExceptionsToAll(firstRowValues.capacityExceptions || []);
  };

  const exceptionsModalStore = exceptionsForStoreId
    ? selectedStores.find((s) => s.id === exceptionsForStoreId)
    : null;

  return (
    <section className={`ncss-col-sm-12 ${isBopis ? 'o2o-bulk-section-centered' : 'ta-sm-l'}`}>
      <h2 className="headline-3 mb2-sm">{`Current Values (${selectedStores.length} store${selectedStores.length === 1 ? '' : 's'})`}</h2>
      <p className="o2o-bulk-empty-hint">
        Edit values inline. Only rows you change will be submitted.
        {firstStoreAlias && selectedStores.length > 1 && (
          <>
            {' '}
            Click <strong>Apply to all</strong> in a column header to copy the first row&apos;s value (
            <strong>{firstStoreAlias}</strong>
            ) to every row.
          </>
        )}
      </p>
      <div className={`o2o-bulk-editable-values${isBopis ? ' is-bopis' : ''}`}>
        <table className="o2o-bulk-editable-table">
          <thead>
            {isBopis
              ? renderBopisHeader({ applyFromFirstRow, canApply, isDisabled })
              : renderSfsHeader({
                applyAllDailyCapacityFromFirstRow,
                applyExceptionsFromFirstRow,
                applyFromFirstRow,
                canApply,
                isDisabled,
              })}
          </thead>
          <tbody>
            {selectedStores.length === 0 && (
              <tr>
                <td className="o2o-bulk-pane-empty" colSpan={isBopis ? 4 : 11}>
                  No stores selected. Go back to Step 1 to add stores.
                </td>
              </tr>
            )}
            {selectedStores.map((store) => {
              const cached = currentValuesCache[store.id];
              const alias = formatStoreAlias(store);
              const current = formValuesByStore[store.id];

              if (cached && cached.fetchError) {
                return (
                  <tr key={store.id} className="o2o-bulk-row has-fetch-error">
                    <td>{alias}</td>
                    <td colSpan={isBopis ? 2 : 9}>
                      <span className="o2o-bulk-row-error">{`Could not load: ${cached.fetchError}`}</span>
                    </td>
                    <td className="o2o-bulk-row-actions">
                      <ButtonRed
                        className="o2o-bulk-remove-btn"
                        confirm={false}
                        isDisabled={isDisabled}
                        label={<i aria-hidden="true" className="g72-x-thick" />}
                        onClick={() => onRemoveStore(store.id)}
                      />
                    </td>
                  </tr>
                );
              }
              if (!cached || !current) {
                return (
                  <tr key={store.id}>
                    <td>{alias}</td>
                    <td colSpan={isBopis ? 2 : 9}><em>Loading...</em></td>
                    <td aria-label="empty" />
                  </tr>
                );
              }

              const dirty = isBopis
                ? isBopisRowDirty(current, cached)
                : isSfsRowDirty(current, cached);
              const rowValid = isBopis
                ? isBopisRowValid(current)
                : isSfsRowValid(current);
              const original = isBopis
                ? deriveBopisFormValues(cached)
                : deriveSfsFormValues(cached);

              const rowClassNames = ['o2o-bulk-row'];
              if (dirty) rowClassNames.push('is-dirty');
              if (!rowValid) rowClassNames.push('is-invalid');

              const actionsCell = (
                <td className="o2o-bulk-row-actions">
                  <ButtonWhite
                    className="border o2o-bulk-reset-btn"
                    isDisabled={isDisabled || !dirty}
                    label="Reset"
                    onClick={() => onResetRow(store.id)}
                  />
                  <ButtonRed
                    className="o2o-bulk-remove-btn"
                    confirm={false}
                    isDisabled={isDisabled}
                    label={<i aria-hidden="true" className="g72-x-thick" />}
                    onClick={() => onRemoveStore(store.id)}
                  />
                </td>
              );

              if (isBopis) {
                const enabledDirty = current.enabled !== original.enabled;
                const ldt = current.leadTimeData;
                const oldLdt = original.leadTimeData;
                const ltDirty = ldt.hours !== oldLdt.hours
                  || ldt.minutes !== oldLdt.minutes
                  || ldt.seconds !== oldLdt.seconds;
                return (
                  <tr key={store.id} className={rowClassNames.join(' ')}>
                    <td>{alias}</td>
                    <td className={`o2o-bulk-checkbox-cell${enabledDirty ? ' is-dirty' : ''}`}>
                      <div className="o2o-bulk-checkbox-center">
                        <Checkbox
                          id={`o2o-bulk-enabled-${store.id}`}
                          isChecked={current.enabled}
                          isDisabled={isDisabled}
                          label=""
                          onChange={({ target: { checked } }) => onUpdateRowValues(store.id, { enabled: checked })}
                        />
                      </div>
                    </td>
                    <td className={`o2o-bulk-leadtime-cell${ltDirty ? ' is-dirty' : ''}`}>
                      <div className="o2o-bulk-leadtime-inputs">
                        <input
                          aria-label="Hours"
                          className="o2o-bulk-cell-input"
                          disabled={isDisabled}
                          id={`o2o-bulk-h-${store.id}`}
                          max="24"
                          min="0"
                          step="1"
                          type="number"
                          value={ldt.hours}
                          onChange={({ target }) => onUpdateRowValues(store.id, {
                            leadTimeData: { ...ldt, hours: clampLeadTimePart(target.value, 24) },
                          })}
                        />
                        <input
                          aria-label="Minutes"
                          className="o2o-bulk-cell-input"
                          disabled={isDisabled}
                          id={`o2o-bulk-m-${store.id}`}
                          max="59"
                          min="0"
                          step="1"
                          type="number"
                          value={ldt.minutes}
                          onChange={({ target }) => onUpdateRowValues(store.id, {
                            leadTimeData: { ...ldt, minutes: clampLeadTimePart(target.value, 59) },
                          })}
                        />
                        <input
                          aria-label="Seconds"
                          className="o2o-bulk-cell-input"
                          disabled={isDisabled}
                          id={`o2o-bulk-s-${store.id}`}
                          max="59"
                          min="0"
                          step="1"
                          type="number"
                          value={ldt.seconds}
                          onChange={({ target }) => onUpdateRowValues(store.id, {
                            leadTimeData: { ...ldt, seconds: clampLeadTimePart(target.value, 59) },
                          })}
                        />
                      </div>
                    </td>
                    {actionsCell}
                  </tr>
                );
              }

              // SFS row
              const enabledDirty = current.enabled !== original.enabled;
              const exceptionCount = (current.capacityExceptions || []).length;
              const exceptionsDirty = JSON.stringify(current.capacityExceptions || []) !== JSON.stringify(original.capacityExceptions || []);

              return (
                <tr key={store.id} className={rowClassNames.join(' ')}>
                  <td>{alias}</td>
                  <td className={`o2o-bulk-checkbox-cell${enabledDirty ? ' is-dirty' : ''}`}>
                    <div className="o2o-bulk-checkbox-center">
                      <Checkbox
                        id={`o2o-bulk-enabled-${store.id}`}
                        isChecked={current.enabled}
                        isDisabled={isDisabled}
                        label=""
                        onChange={({ target: { checked } }) => onUpdateRowValues(store.id, { enabled: checked })}
                      />
                    </div>
                  </td>
                  {daysOfTheWeek.map((day) => {
                    const currentVal = (current.defaultCapacity[day] || { maxUnits: 0 }).maxUnits;
                    const originalVal = (original.defaultCapacity[day] || { maxUnits: 0 }).maxUnits;
                    const cellDirty = currentVal !== originalVal;
                    return (
                      <NumberCell
                        key={day}
                        id={`o2o-bulk-${day}-${store.id}`}
                        isDirty={cellDirty}
                        isDisabled={isDisabled}
                        value={currentVal}
                        onChange={(maxUnits) => onUpdateRowValues(store.id, {
                          defaultCapacity: {
                            ...current.defaultCapacity,
                            [day]: { maxUnits },
                          },
                        })}
                      />
                    );
                  })}
                  <td className={`o2o-bulk-exceptions-cell${exceptionsDirty ? ' is-dirty' : ''}`}>
                    <ButtonWhite
                      className="border o2o-bulk-exceptions-btn"
                      isDisabled={isDisabled}
                      label={`Edit (${exceptionCount})`}
                      onClick={() => setExceptionsForStoreId(store.id)}
                    />
                  </td>
                  {actionsCell}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <SfsExceptionsModal
        initialExceptions={
          exceptionsModalStore
            ? (formValuesByStore[exceptionsModalStore.id] || {}).capacityExceptions || []
            : []
        }
        isOpen={!!exceptionsModalStore}
        storeAlias={exceptionsModalStore ? formatStoreAlias(exceptionsModalStore) : ''}
        onCancel={() => setExceptionsForStoreId(null)}
        onSave={(updated) => {
          if (exceptionsModalStore) {
            onUpdateRowValues(exceptionsModalStore.id, { capacityExceptions: updated });
          }
          setExceptionsForStoreId(null);
        }}
      />
    </section>
  );
};

EditableValuesTable.defaultProps = {
  isDisabled: false,
};

EditableValuesTable.propTypes = {
  currentValuesCache: PropTypes.shape().isRequired,
  formValuesByStore: PropTypes.shape().isRequired,
  fulfillmentType: PropTypes.oneOf(['BOPIS', 'SHIP_FROM_STORE']).isRequired,
  isDisabled: PropTypes.bool,
  onApplyDayToAll: PropTypes.func.isRequired,
  onApplyEnabledToAll: PropTypes.func.isRequired,
  onApplyExceptionsToAll: PropTypes.func.isRequired,
  onApplyLeadTimeToAll: PropTypes.func.isRequired,
  onRemoveStore: PropTypes.func.isRequired,
  onResetRow: PropTypes.func.isRequired,
  onUpdateRowValues: PropTypes.func.isRequired,
  selectedStores: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
};

export default EditableValuesTable;
